import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Divider,
  Select,
  Grid,
  VStack,
  Container,
  useColorModeValue,
  Textarea,
  FormErrorMessage,
} from "@chakra-ui/react";
import { supabase } from "./supabaseClient";
import { useToastContext } from "./ToastProvider";
import { TURKEY_CITIES, PRIORITY_CITIES, CITY_DISTRICTS } from "./constants/cities";
import PhoneInput, { formatPhoneForDb } from "./components/PhoneInput";
import { validateCustomerForm, getFirstError } from "./validation/customerSchema";
import { checkCustomerExists } from "./services/customerService";

const CustomerForm = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("İstanbul");
  const [ilce, setIlce] = useState("");
  const [primaryPeople, setPrimaryPeople] = useState("");
  const [primaryPeoplePos, setPrimaryPeoplePos] = useState("");
  const [primaryPeoplePhone, setPrimaryPeoplePhone] = useState("");
  const [primaryPeopleEmail, setPrimaryPeopleEmail] = useState("");
  const [secondaryPeople, setSecondaryPeople] = useState("");
  const [secondaryPeoplePos, setSecondaryPeoplePos] = useState("");
  const [secondaryPeoplePhone, setSecondaryPeoplePhone] = useState("");
  const [secondaryPeopleEmail, setSecondaryPeopleEmail] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("\u{1F1F9}\u{1F1F7} +90");
  const [primaryPeoplePhoneCC, setPrimaryPeoplePhoneCC] = useState("\u{1F1F9}\u{1F1F7} +90");
  const [secondaryPeoplePhoneCC, setSecondaryPeoplePhoneCC] = useState("\u{1F1F9}\u{1F1F7} +90");
  const [notes, setNotes] = useState("");
  const [customerStatus, setCustomerStatus] = useState("aktif");
  const [uid, setUid] = useState("");
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showToast = useToastContext();

  const bgColor = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  // Get available districts based on selected city
  const availableDistricts = city ? (CITY_DISTRICTS[city] || []) : [];

  // Handle city change - reset district when city changes
  const handleCityChange = (e) => {
    const newCity = e.target.value;
    setCity(newCity);
    setIlce(""); // Reset district when city changes
  };

  useEffect(() => {
    const fetchUserUid = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUid(user.id);
      }
    };
    fetchUserUid();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);

    // Prepare form data for validation
    const formData = {
      name,
      email,
      phone,
      address,
      city,
      ilce,
      primaryPeople,
      primaryPeoplePos,
      primaryPeoplePhone,
      primaryPeopleEmail,
      secondaryPeople,
      secondaryPeoplePos,
      secondaryPeoplePhone,
      secondaryPeopleEmail,
      customerStatus,
      notes,
    };

    // Run Zod validation
    const validation = validateCustomerForm(formData);

    if (!validation.success) {
      setErrors(validation.errors);
      const firstError = getFirstError(validation.errors);
      showToast({
        title: "Hata!",
        description: firstError || "Tüm gerekli alanları doldurun.",
        status: "error",
      });
      setIsSubmitting(false);
      return;
    }

    // Build formatted phone numbers for DB
    const formattedPhone = formatPhoneForDb(phoneCountryCode, phone);
    const formattedPrimaryPhone = formatPhoneForDb(primaryPeoplePhoneCC, primaryPeoplePhone);
    const formattedSecondaryPhone = formatPhoneForDb(secondaryPeoplePhoneCC, secondaryPeoplePhone);

    // Check for duplicate customer
    try {
      const duplicateCheck = await checkCustomerExists(
        formattedPhone,
        formattedPrimaryPhone,
        name,
        email
      );

      if (duplicateCheck && duplicateCheck.exists) {
        showToast({
          title: "Kayıt engellendi!",
          description: duplicateCheck.message || "Bu müşteri zaten sistemde mevcut!",
          status: "warning",
          duration: 5000,
        });
        setIsSubmitting(false);
        return;
      }
    } catch (error) {
      console.error("Duplicate check failed:", error);
      showToast({
        title: "Hata!",
        description: "Müşteri kontrolü sırasında bir hata oluştu.",
        status: "error",
      });
      setIsSubmitting(false);
      return;
    }

    // Get fresh user ID for created_from
    const { data: { user } } = await supabase.auth.getUser();
    const currentUid = user?.id || uid;

    const { data, error } = await supabase.from("customers").insert([
      {
        name,
        email,
        phone: formattedPhone,
        address,
        city,
        ilce,
        primary_people: primaryPeople,
        primary_people_pos: primaryPeoplePos,
        primary_people_phone: formattedPrimaryPhone,
        primary_people_email: primaryPeopleEmail,
        secondary_people: secondaryPeople,
        secondary_people_pos: secondaryPeoplePos,
        secondary_people_phone: formattedSecondaryPhone,
        secondary_people_email: secondaryPeopleEmail,
        customer_status: customerStatus,
        created_from: currentUid,
        notes,
        status: "pending",
      },
    ]);

    if (error) {
      let description = "Bir hata meydana geldi.";
      if (error.message?.includes("DUPLICATE_NAME")) {
        const dupName = error.message.split("DUPLICATE_NAME:")[1];
        description = `"${dupName}" adlı müşteri zaten sistemde kayıtlı.`;
      } else if (error.message?.includes("DUPLICATE_PHONE")) {
        const dupName = error.message.split("DUPLICATE_PHONE:")[1];
        description = `Bu telefon numarası zaten "${dupName}" müşterisine ait.`;
      } else if (error.message?.includes("DUPLICATE_EMAIL")) {
        const dupName = error.message.split("DUPLICATE_EMAIL:")[1];
        description = `Bu e-posta adresi zaten "${dupName}" müşterisine ait.`;
      }
      showToast({
        title: error.message?.includes("DUPLICATE") ? "Kayıt engellendi!" : "Hata!",
        description,
        status: error.message?.includes("DUPLICATE") ? "warning" : "error",
        duration: 5000,
      });
      console.error("Hata:", error);
      setIsSubmitting(false);
      return;
    }

    console.log("Müşteri kaydedildi:", data);

    showToast({
      title: "Başarılı!",
      description: "Kayıt başarıyla oluşturuldu! Yönetici onayı bekleniyor.",
      status: "success",
    });

    // Reset form fields
    setName("");
    setEmail("");
    setPhone("");
    setPhoneCountryCode("\u{1F1F9}\u{1F1F7} +90");
    setAddress("");
    setCity("");
    setIlce("");
    setPrimaryPeople("");
    setPrimaryPeoplePos("");
    setPrimaryPeoplePhone("");
    setPrimaryPeoplePhoneCC("\u{1F1F9}\u{1F1F7} +90");
    setPrimaryPeopleEmail("");
    setSecondaryPeople("");
    setSecondaryPeoplePos("");
    setSecondaryPeoplePhone("");
    setSecondaryPeoplePhoneCC("\u{1F1F9}\u{1F1F7} +90");
    setSecondaryPeopleEmail("");
    setCustomerStatus("");
    setNotes("");
    setErrors({});
    setIsSubmitting(false);

    navigate("/customers");
  };

  return (
    <Container maxW="6xl" p={2}>
      <Box
        bg={bgColor}
        borderRadius="lg"
        p={8}
        boxShadow="lg"
        borderWidth={1}
        borderColor={borderColor}
      >
        <VStack spacing={8} align="stretch">
          <Heading size="lg" textAlign="center">
            Yeni Müşteri Ekle
          </Heading>
          <form onSubmit={handleSubmit}>
            <VStack spacing={8}>
              <Box width="100%">
                <Heading size="md" mb={4}>
                  Firma Bilgileri
                </Heading>
                <Divider mb={4} />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    md: "repeat(2, 1fr)",
                  }}
                  gap={6}
                >
                  <FormControl isRequired isInvalid={!!errors.name}>
                    <FormLabel>Firma Adı</FormLabel>
                    <Input
                      type="text"
                      value={name}
                      required={true}
                      onChange={(e) => setName(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.name}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!errors.email}>
                    <FormLabel>E-posta</FormLabel>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.email}</FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.phone}>
                    <FormLabel htmlFor="phone-number">Telefon</FormLabel>
                    <PhoneInput
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      countryCode={phoneCountryCode}
                      onCountryCodeChange={setPhoneCountryCode}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.phone}</FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.address}>
                    <FormLabel>Adres</FormLabel>
                    <Textarea
                      type="text"
                      value={address}
                      required={true}
                      onChange={(e) => setAddress(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.address}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.city}>
                    <FormLabel>Şehir</FormLabel>
                    <Select
                      value={city}
                      onChange={handleCityChange}
                      focusBorderColor="yellow.400"
                    >
                      {PRIORITY_CITIES.map((cityOption) => (
                        <option key={cityOption} value={cityOption}>
                          {cityOption}
                        </option>
                      ))}
                      {TURKEY_CITIES
                        .filter((cityOption) => !PRIORITY_CITIES.includes(cityOption))
                        .map((cityOption) => (
                          <option key={cityOption} value={cityOption}>
                            {cityOption}
                          </option>
                        ))}
                    </Select>
                    <FormErrorMessage>{errors.city}</FormErrorMessage>
                  </FormControl>
                  <FormControl>
                    <FormLabel>İlçe</FormLabel>
                    <Select
                      value={ilce}
                      onChange={(e) => setIlce(e.target.value)}
                      focusBorderColor="yellow.400"
                      isDisabled={!city || availableDistricts.length === 0}
                      placeholder="İlçe seçin"
                    >
                      {availableDistricts.map((district) => (
                        <option key={district} value={district}>
                          {district}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Box>
              <Box width="100%" mt={8}>
                <Heading size="md" mb={4}>
                  Birincil Yetkili
                </Heading>
                <Divider my={2} />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(2, 1fr)",
                  }}
                  gap={5}
                >
                  <FormControl isRequired isInvalid={!!errors.primaryPeople}>
                    <FormLabel>Birincil Yetkili Kişi</FormLabel>
                    <Input
                      type="text"
                      value={primaryPeople}
                      required={true}
                      onChange={(e) => setPrimaryPeople(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.primaryPeople}</FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.primaryPeoplePos}>
                    <FormLabel>Birincil Yetkili Pozisyonu</FormLabel>
                    <Input
                      type="text"
                      value={primaryPeoplePos}
                      required={true}
                      onChange={(e) => setPrimaryPeoplePos(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.primaryPeoplePos}</FormErrorMessage>
                  </FormControl>
                  <FormControl isRequired isInvalid={!!errors.primaryPeoplePhone}>
                    <FormLabel htmlFor="primaryPeoplePhone-number">Birincil Yetkili Telefon</FormLabel>
                    <PhoneInput
                      id="primaryPeoplePhone"
                      value={primaryPeoplePhone}
                      onChange={(e) => setPrimaryPeoplePhone(e.target.value)}
                      countryCode={primaryPeoplePhoneCC}
                      onCountryCodeChange={setPrimaryPeoplePhoneCC}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.primaryPeoplePhone}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.primaryPeopleEmail}>
                    <FormLabel>Birincil Yetkili E-posta</FormLabel>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={primaryPeopleEmail}
                      onChange={(e) => setPrimaryPeopleEmail(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.primaryPeopleEmail}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </Box>

              <Box width="100%" mt={8}>
                <Heading size="md" mb={4}>
                  İkincil Yetkili
                </Heading>
                <Divider my={2} />
                <Grid
                  templateColumns={{
                    base: "repeat(1, 1fr)",
                    lg: "repeat(2, 1fr)",
                  }}
                  gap={5}
                >
                  <FormControl>
                    <FormLabel>İkincil Yetkili Kişi</FormLabel>
                    <Input
                      type="text"
                      value={secondaryPeople}
                      onChange={(e) => setSecondaryPeople(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>İkincil Yetkili Pozisyonu</FormLabel>
                    <Input
                      type="text"
                      value={secondaryPeoplePos}
                      onChange={(e) => setSecondaryPeoplePos(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                  </FormControl>
                  <FormControl isInvalid={!!errors.secondaryPeoplePhone}>
                    <FormLabel htmlFor="secondaryPeoplePhone-number">İkincil Yetkili Telefon</FormLabel>
                    <PhoneInput
                      id="secondaryPeoplePhone"
                      value={secondaryPeoplePhone}
                      onChange={(e) => setSecondaryPeoplePhone(e.target.value)}
                      countryCode={secondaryPeoplePhoneCC}
                      onCountryCodeChange={setSecondaryPeoplePhoneCC}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.secondaryPeoplePhone}</FormErrorMessage>
                  </FormControl>
                  <FormControl isInvalid={!!errors.secondaryPeopleEmail}>
                    <FormLabel>İkincil Yetkili E-posta</FormLabel>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={secondaryPeopleEmail}
                      onChange={(e) => setSecondaryPeopleEmail(e.target.value)}
                      focusBorderColor="yellow.400"
                    />
                    <FormErrorMessage>{errors.secondaryPeopleEmail}</FormErrorMessage>
                  </FormControl>
                </Grid>
              </Box>
              {/*
              <Box width="100%">
                <Heading size="md" mb={4}>
                  Görüşme Notu
                </Heading>
                <Divider mb={4} />
                <FormControl>
                  <Textarea
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    focusBorderColor="yellow.400"
                  />
                </FormControl>
              </Box>
              */}

              <Box width="100%">
                <Heading size="md" mb={4}>
                  Müşteri Durumu
                </Heading>
                <Divider mb={4} />
                <FormControl isInvalid={!!errors.customerStatus}>
                  <FormLabel>Durum</FormLabel>
                  <Select
                    value={customerStatus}
                    onChange={(e) => setCustomerStatus(e.target.value)}
                    focusBorderColor="yellow.400"
                  >
                    <option value="aktif">Aktif</option>
                    <option value="eski">Eski</option>
                    <option value="potansiyel">Potansiyel</option>
                  </Select>
                  <FormErrorMessage>{errors.customerStatus}</FormErrorMessage>
                </FormControl>
              </Box>

              <Button
                type="submit"
                backgroundColor="yellow.400"
                _hover={{ backgroundColor: "yellow.500" }}
                size="lg"
                width="full"
                isLoading={isSubmitting}
                loadingText="Kaydediliyor..."
              >
                Müşteri Ekle
              </Button>
            </VStack>
          </form>
        </VStack>
      </Box>
    </Container>
  );
};

export default CustomerForm;
